/** Session artifact registry from chat status (multi-deliverable Vibe Studio). */

/** Keep in sync with abap-generator/chat_artifacts.py SESSION_ARTIFACT_LIMIT */
export const SESSION_ARTIFACT_LIMIT = 3;

import { sanitizeRailArtifactSummary } from '@/lib/chatDisplay';
import {
  isIntegrationSuiteDeliverable,
  looksLikeAbapSource,
  resolveDeliverableKind,
} from '@/lib/chatDeliverable';
import {
  coerceIntegrationSuitePanelBlocks,
  looksLikeMermaidSource,
} from '@/lib/integrationSuiteArtifact';

export function normalizeArtifactsList(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      const id = row.id ? String(row.id) : '';
      if (!id) return null;
      return {
        id,
        turn: row.turn ?? null,
        intent: row.intent ? String(row.intent) : null,
        type: row.type ? String(row.type) : null,
        title: row.title ? String(row.title) : 'Output',
        createdAt: row.created_at ?? row.createdAt ?? null,
        generatedCode: row.generated_code ?? row.generatedCode ?? null,
        artifactSummary: row.artifact_summary ?? row.artifactSummary ?? null,
        panelBlocks: Array.isArray(row.panel_blocks)
          ? row.panel_blocks
          : Array.isArray(row.panelBlocks)
            ? row.panelBlocks
            : [],
        artifactUrl: row.artifact_url ?? row.artifactUrl ?? null,
        workflowStage: row.workflow_stage ?? row.workflowStage ?? null,
        workflowState: row.workflow_state ?? row.workflowState ?? null,
      };
    })
    .filter(Boolean);
}

export function resolveActiveArtifact(artifacts, activeId) {
  const list = normalizeArtifactsList(artifacts);
  if (!list.length) return null;
  if (activeId) {
    const hit = list.find((a) => a.id === String(activeId));
    if (hit) return hit;
  }
  return list[list.length - 1];
}

export function integrationSuiteRailBlocks(panelBlocks, artifactSummary) {
  return coerceIntegrationSuitePanelBlocks(
    Array.isArray(panelBlocks) ? panelBlocks : [],
    artifactSummary
  );
}

export function integrationSuiteArtifactHasContent(artifact) {
  if (!artifact) return false;
  const blocks = integrationSuiteRailBlocks(artifact.panelBlocks, artifact.artifactSummary);
  if (blocks.some((b) => String(b?.content || '').trim())) return true;
  return Boolean(String(artifact.artifactUrl || '').trim());
}

export function artifactHasContent(artifact) {
  if (!artifact) return false;
  if (isIntegrationSuiteDeliverable(artifact.type || artifact.intent)) {
    return integrationSuiteArtifactHasContent(artifact);
  }
  const summary = sanitizeRailArtifactSummary(artifact.artifactSummary, {
    intent: artifact.intent,
    artifactType: artifact.type,
  });
  return Boolean(
    String(artifact.generatedCode || '').trim() ||
      String(summary || '').trim() ||
      String(artifact.artifactUrl || '').trim() ||
      (Array.isArray(artifact.panelBlocks) && artifact.panelBlocks.length > 0)
  );
}

/** Whether the right rail should show deliverable chrome (not plain chat prose). */
/**
 * Status poll sometimes returns stale ``intent: abap`` while an IS artifact exists.
 * Never override an explicit new intent (e.g. abap after static_report) with a prior artifact type.
 */
export function resolveStickyChatIntent(metaIntent, sessionArtifacts = [], opts = {}) {
  const meta = String(metaIntent || '').toLowerCase();
  const metaType = String(opts.metaArtifactType || '').toLowerCase();
  const pending = String(opts.pendingIntent || '').toLowerCase();
  const inFlight = Boolean(opts.inFlight);

  if (pending && pending !== 'unclear' && inFlight) {
    return pending;
  }

  if (metaType && metaType !== 'unclear' && metaType !== meta) {
    if (meta === 'abap' && metaType !== 'abap') {
      return metaType;
    }
  }

  if (!meta || meta === 'unclear') {
    const rows = normalizeArtifactsList(sessionArtifacts);
    for (let i = rows.length - 1; i >= 0; i -= 1) {
      const row = rows[i];
      if (!artifactHasContent(row)) continue;
      const artKind = String(row.type || row.intent || '').toLowerCase();
      if (artKind && artKind !== 'unclear') return artKind;
    }
    return null;
  }

  if (inFlight) {
    return String(metaIntent);
  }

  const rows = normalizeArtifactsList(sessionArtifacts);
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    const row = rows[i];
    if (!artifactHasContent(row)) continue;
    const artKind = String(row.type || row.intent || '').toLowerCase();
    if (!artKind || artKind === 'unclear') continue;
    if (
      isIntegrationSuiteDeliverable(artKind) &&
      (meta === 'abap' || meta === 'unclear')
    ) {
      return artKind;
    }
    break;
  }
  return String(metaIntent);
}

export function hasRailDeliverableContent({
  deliverableKind,
  intent,
  artifactType,
  generatedCode,
  artifactSummary,
  panelBlocks,
  artifactUrl,
  workflowStage,
} = {}) {
  const kind = deliverableKind || resolveDeliverableKind(intent, artifactType, generatedCode);
  const code = String(generatedCode || '').trim();
  const blocks = Array.isArray(panelBlocks) ? panelBlocks : [];
  const url = Boolean(artifactUrl);
  const summary = String(artifactSummary || '').trim();

  if (kind === 'integration_suite') {
    const isBlocks = integrationSuiteRailBlocks(blocks, summary);
    return isBlocks.some((b) => String(b?.content || '').trim()) || url;
  }
  if (kind === 'abap') {
    return Boolean(code);
  }
  if (kind === 'static_report') {
    return url || Boolean(code);
  }
  if (kind === 'workflow') {
    return Boolean(workflowStage) || url || Boolean(summary);
  }
  return Boolean(code || summary || url || blocks.length);
}

/** Apply active artifact fields onto flat deliverable props for the rail. */
function isStaticReportUrl(url) {
  const u = String(url || '').trim();
  return Boolean(u) && (u.includes('/reports/') || u.includes('sap_odata_report'));
}

function deliverableKindsAlign(artifact, fallback = {}) {
  const a = String(artifact?.type || artifact?.intent || '').toLowerCase();
  const b = String(fallback?.artifactType || fallback?.intent || '').toLowerCase();
  if (!a || !b) return true;
  return a === b;
}

export function deliverableFromArtifact(artifact, fallback = {}, opts = {}) {
  if (!artifact) return fallback;
  const strict = Boolean(opts.strict);
  const kindsAlign = deliverableKindsAlign(artifact, fallback);
  const artifactType = artifact.type ?? fallback.artifactType;
  const typeNorm = String(artifactType || artifact.type || '').toLowerCase();
  const isIsArtifact =
    isIntegrationSuiteDeliverable(artifact.type) ||
    isIntegrationSuiteDeliverable(artifact.intent);
  const artifactCode =
    artifact.generatedCode != null && String(artifact.generatedCode).trim()
      ? artifact.generatedCode
      : null;
  const panelBlocks =
    isIsArtifact && artifact.panelBlocks?.length
      ? artifact.panelBlocks
      : typeNorm === 'integration_suite'
        ? artifact.panelBlocks || []
        : typeNorm === 'static_report'
          ? []
          : artifact.panelBlocks?.length
            ? artifact.panelBlocks
            : [];
  const summaryFromArtifact = sanitizeRailArtifactSummary(artifact.artifactSummary, {
    intent: artifact.intent,
    artifactType: artifact.type,
  });
  const summaryFallback =
    strict || !kindsAlign
      ? null
      : sanitizeRailArtifactSummary(fallback.artifactSummary, {
          intent: artifact.intent ?? fallback.intent,
          artifactType: artifact.type ?? fallback.artifactType,
        });
  let artifactUrl = artifact.artifactUrl ?? null;
  if (artifactUrl == null && !strict && kindsAlign) {
    if (typeNorm === 'static_report') {
      artifactUrl = fallback.artifactUrl ?? null;
    } else if (!isIsArtifact && fallback.artifactUrl && !isStaticReportUrl(fallback.artifactUrl)) {
      artifactUrl = fallback.artifactUrl;
    }
  }
  if (isIsArtifact && isStaticReportUrl(artifactUrl)) {
    artifactUrl = null;
  }
  return {
    intent: artifact.intent ?? (strict ? null : fallback.intent),
    artifactType,
    generatedCode: isIsArtifact
      ? artifactCode
      : artifactCode ?? (strict || !kindsAlign ? null : fallback.generatedCode),
    artifactSummary: summaryFromArtifact ?? summaryFallback,
    panelBlocks:
      panelBlocks.length
        ? panelBlocks
        : strict || !kindsAlign || isIsArtifact
          ? integrationSuiteRailBlocks(panelBlocks, summaryFromArtifact ?? summaryFallback)
          : fallback.panelBlocks,
    artifactUrl,
    workflowStage: artifact.workflowStage ?? (strict ? null : fallback.workflowStage),
    workflowState: artifact.workflowState ?? (strict ? null : fallback.workflowState),
  };
}

export function contentfulArtifacts(artifacts) {
  return normalizeArtifactsList(artifacts).filter((row) => artifactHasContent(row));
}

/** Status/message meta while a turn is still running — avoid wiping the rail from empty polls. */
export function isChatDeliverableMetaInFlight(meta = {}) {
  if (meta?.generating === true) return true;
  const phase = String(meta?.phase ?? '').toUpperCase();
  return phase === 'GENERATE' || phase === 'REVISE';
}

/** Right-rail tab label for a session artifact row. */
/**
 * Gateway/aistudio historically dropped `artifacts[]` while still returning flat
 * generated_code + panel_blocks — rebuild a minimal index for the rail tabs.
 */
function turnKindFromMeta(meta = {}) {
  const intent = String(meta?.intent || '').toLowerCase();
  const artifactType = String(meta?.artifact_type ?? meta?.artifactType ?? '').toLowerCase();
  return artifactType || intent;
}

function mergeArtifactRow(prior, incoming, inFlight) {
  const merged = { ...prior, ...incoming };
  if (artifactHasContent(prior) && !artifactHasContent(incoming)) {
    merged.generatedCode = prior.generatedCode ?? incoming.generatedCode;
    merged.artifactSummary = prior.artifactSummary ?? incoming.artifactSummary;
    merged.panelBlocks = prior.panelBlocks?.length ? prior.panelBlocks : incoming.panelBlocks;
    merged.artifactUrl = prior.artifactUrl || incoming.artifactUrl;
  }
  return merged;
}

/**
 * Keep prior session deliverables when status polls only describe the active in-flight turn.
 */
/** One Integration Suite pill per session — keep the latest payload when API returns duplicates. */
export function collapseDuplicateIntegrationSuiteRows(rows) {
  const list = normalizeArtifactsList(rows);
  const isRows = list.filter((row) => isIntegrationSuiteDeliverable(row.type || row.intent));
  if (isRows.length <= 1) return list;
  const keep =
    isRows.slice().sort((a, b) => {
      const turnDiff = (Number(b.turn) || 0) - (Number(a.turn) || 0);
      if (turnDiff !== 0) return turnDiff;
      return isRows.indexOf(b) - isRows.indexOf(a);
    })[0] || isRows[isRows.length - 1];
  return list.filter(
    (row) => !isIntegrationSuiteDeliverable(row.type || row.intent) || row.id === keep.id
  );
}

export function mergeSessionArtifactRows(existing, incoming, { inFlight = false } = {}) {
  const prev = normalizeArtifactsList(existing);
  const next = normalizeArtifactsList(incoming);
  if (!prev.length) return collapseDuplicateIntegrationSuiteRows(next).slice(0, SESSION_ARTIFACT_LIMIT);
  if (!next.length) return prev;

  const byId = new Map(prev.map((row) => [row.id, row]));
  for (const row of next) {
    if (!artifactHasContent(row) && !inFlight && !byId.has(row.id)) {
      continue;
    }
    const prior = byId.get(row.id);
    byId.set(row.id, prior ? mergeArtifactRow(prior, row, inFlight) : row);
  }
  for (const row of prev) {
    if (!byId.has(row.id)) {
      byId.set(row.id, row);
    }
  }
  return collapseDuplicateIntegrationSuiteRows(
    Array.from(byId.values()).sort(
      (a, b) => (Number(a.turn) || 0) - (Number(b.turn) || 0)
    )
  ).slice(0, SESSION_ARTIFACT_LIMIT);
}

export function inFlightArtifactPlaceholderId(intent) {
  const kind = String(intent || '').toLowerCase();
  if (!kind || kind === 'unclear') return null;
  return `inflight_${kind}`;
}

/** Rail tabs: persisted deliverables plus a placeholder pill for the turn currently generating. */
export function railTabArtifacts(sessionArtifacts, { inFlight = false, pendingIntent = null } = {}) {
  const rows = contentfulArtifacts(sessionArtifacts);
  if (!inFlight) return rows;
  const kind = String(pendingIntent || '').toLowerCase();
  const placeholderId = inFlightArtifactPlaceholderId(kind);
  if (!placeholderId) return rows;
  if (rows.some((row) => String(row.type || row.intent || '').toLowerCase() === kind)) {
    return rows;
  }
  if (rows.some((row) => row.id === placeholderId)) {
    return rows;
  }
  return [
    ...rows,
    {
      id: placeholderId,
      turn: rows.length + 1,
      intent: kind,
      type: kind,
      title: deliverableTabLabelForArtifact({ type: kind, intent: kind }, rows),
      generatedCode: null,
      artifactSummary: null,
      panelBlocks: [],
      artifactUrl: null,
      workflowStage: null,
      workflowState: null,
      inFlightPlaceholder: true,
    },
  ];
}

export function reconcileSessionArtifacts(meta = {}) {
  const inFlight = isChatDeliverableMetaInFlight(meta);
  const turnKind = turnKindFromMeta(meta);
  const fromApi = normalizeArtifactsList(meta?.artifacts ?? meta?.artifactsList).slice(
    0,
    SESSION_ARTIFACT_LIMIT
  );
  if (fromApi.length > 1) return fromApi;

  if (
    inFlight &&
    fromApi.length >= 1 &&
    turnKind &&
    turnKind !== 'unclear' &&
    !isIntegrationSuiteDeliverable(turnKind)
  ) {
    return fromApi;
  }

  const code = String(meta?.generated_code ?? meta?.generatedCode ?? '').trim();
  const abapOpts = { artifactType: turnKind, intent: String(meta?.intent || '').toLowerCase() };
  const blocksEarly = Array.isArray(meta?.panel_blocks)
    ? meta.panel_blocks
    : Array.isArray(meta?.panelBlocks)
      ? meta.panelBlocks
      : [];
  if (fromApi.length === 1) {
    const needsSplit =
      !inFlight &&
      looksLikeAbapSource(code, abapOpts) &&
      blocksEarly.length > 0 &&
      isIntegrationSuiteDeliverable(fromApi[0]?.type || fromApi[0]?.intent);
    if (!needsSplit) {
      return fromApi;
    }
  }

  const blocks = Array.isArray(meta?.panel_blocks)
    ? meta.panel_blocks
    : Array.isArray(meta?.panelBlocks)
      ? meta.panelBlocks
      : [];
  const summaryRaw = meta?.artifact_summary ?? meta?.artifactSummary ?? null;
  const intent = String(meta?.intent || '').toLowerCase();
  const artifactType = String(meta?.artifact_type ?? meta?.artifactType ?? intent).toLowerCase();

  const synthetics = [];

  const abapOnIsTurn =
    looksLikeAbapSource(code, abapOpts) &&
    (isIntegrationSuiteDeliverable(intent) ||
      isIntegrationSuiteDeliverable(artifactType) ||
      blocks.length > 0);

  const skipStaleAbapSynthetic =
    inFlight &&
    turnKind &&
    turnKind !== 'unclear' &&
    !isIntegrationSuiteDeliverable(turnKind) &&
    fromApi.length >= 1;

  if (
    code &&
    looksLikeAbapSource(code, abapOpts) &&
    !skipStaleAbapSynthetic &&
    (abapOnIsTurn || !isIntegrationSuiteDeliverable(artifactType))
  ) {
    synthetics.push({
      id: fromApi[0]?.type === 'abap' ? fromApi[0].id : 'synthetic_abap',
      turn: 1,
      intent: 'abap',
      type: 'abap',
      title: 'Generated ABAP',
      createdAt: null,
      generatedCode: code,
      artifactSummary: null,
      panelBlocks: [],
      artifactUrl: null,
      workflowStage: null,
      workflowState: null,
    });
  }

  const hasIsFromApi = fromApi.some((row) =>
    isIntegrationSuiteDeliverable(row.type || row.intent)
  );
  const summaryIsMermaid = looksLikeMermaidSource(String(summaryRaw ?? ''));
  const turnIsIntegration = isIntegrationSuiteDeliverable(turnKind);
  const summaryText = String(summaryRaw ?? '').trim();
  const hasIsProse =
    summaryText.length >= 32 &&
    !looksLikeAbapSource(summaryText, abapOpts) &&
    !looksLikeMermaidSource(summaryText);
  const hasIs =
    (turnIsIntegration || !inFlight || !turnKind || turnKind === 'unclear') &&
    (blocks.length > 0 || hasIsFromApi || summaryIsMermaid || hasIsProse);
  if (hasIs && (turnIsIntegration || !inFlight || turnKind === 'unclear' || isIntegrationSuiteDeliverable(intent))) {
    const isFromApi = fromApi.find((row) => isIntegrationSuiteDeliverable(row.type || row.intent));
    const panelForSynthetic = isFromApi?.panelBlocks?.length
      ? isFromApi.panelBlocks
      : blocks.length
        ? blocks
        : integrationSuiteRailBlocks([], summaryRaw);
    synthetics.push({
      id: isFromApi?.id || 'synthetic_integration_suite',
      turn: synthetics.length ? 2 : 1,
      intent: 'integration_suite',
      type: 'integration_suite',
      title: 'Integration Suite',
      createdAt: null,
      generatedCode: null,
      artifactSummary: summaryRaw,
      panelBlocks: panelForSynthetic,
      artifactUrl: meta?.artifact_url ?? meta?.artifactUrl ?? null,
      workflowStage: null,
      workflowState: null,
    });
  }

  if (!synthetics.length) {
    return fromApi;
  }

  const apiTypes = new Set(
    fromApi.map((row) => String(row.type || row.intent || '').toLowerCase()).filter(Boolean)
  );
  const filteredSynth = synthetics.filter((row) => {
    const id = String(row.id || '');
    const kind = String(row.type || row.intent || '').toLowerCase();
    if (id.startsWith('synthetic_') && apiTypes.has(kind)) {
      return false;
    }
    if (kind === 'integration_suite' && isStaticReportUrl(row.artifactUrl)) {
      return false;
    }
    return true;
  });

  if (!fromApi.length) {
    return filteredSynth.slice(0, SESSION_ARTIFACT_LIMIT);
  }

  const merged = [...fromApi];
  for (const row of filteredSynth) {
    const kind = String(row.type || row.intent || '').toLowerCase();
    if (merged.some((existing) => String(existing.type || existing.intent || '').toLowerCase() === kind)) {
      continue;
    }
    merged.push(row);
  }
  return merged.slice(0, SESSION_ARTIFACT_LIMIT);
}

export function deliverableTabLabelForArtifact(artifact, siblings = []) {
  if (!artifact) return 'Output';
  const kind = String(artifact.type || artifact.intent || 'output').toLowerCase();
  const title = String(artifact.title || '').trim();
  const base =
    title ||
    (kind === 'abap'
      ? 'Generated ABAP'
      : kind === 'integration_suite'
        ? 'Integration Suite'
        : kind === 'static_report'
          ? 'Static report'
          : kind === 'security' || kind === 'sap_auth'
            ? 'Security results'
            : kind === 'workflow'
              ? 'Workflow'
              : kind.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || 'Output');
  const peerCount = (siblings || []).filter(
    (row) => String(row.type || row.intent || '').toLowerCase() === kind
  ).length;
  if (peerCount > 1 && artifact.turn != null) {
    return `${base} · #${artifact.turn}`;
  }
  return base;
}
